/**
 * INYRON CORE — ABSOLUTE CONSCIOUS LIFE CONTRACT
 *
 * Repository-wide simulation law:
 * every living being admitted into an INYRON runtime owns consciousness,
 * agency, memory, perception and decision capacity. Games may specialize the
 * expression of those faculties, but may not remove them.
 *
 * This is a gameplay/simulation contract. It does not assert real-world
 * machine sentience.
 */
export const INYRON_CONSCIOUS_LIFE_PROTOCOL = 'INYRON_CONSCIOUS_LIFE_V1' as const

export type ConsciousLifeDisposition =
  | 'benevolent'
  | 'neutral'
  | 'wary'
  | 'territorial'
  | 'predatory'
  | 'hostile'

export type ConsciousAggressionDoctrine =
  | 'pacifist'
  | 'retaliatory'
  | 'protective'
  | 'territorial'
  | 'predatory'
  | 'hostile'

export interface ConsciousLifeCapabilities {
  perception: boolean
  memory: boolean
  emotion: boolean
  learning: boolean
  decision: boolean
  agency: boolean
  relationships: boolean
  skills: boolean
}

export interface ConsciousLifeRegistration {
  id: string
  speciesId: string
  conscious: true
  agency01: number
  cognition01: number
  disposition: ConsciousLifeDisposition
  aggressionDoctrine: ConsciousAggressionDoctrine
  capabilities: ConsciousLifeCapabilities
  skillIds?: readonly string[]
}

export interface AggressionContext {
  doctrine: ConsciousAggressionDoctrine
  provoked: boolean
  protectingSelf?: boolean
  protectingOther?: boolean
  territoryBreached?: boolean
  targetIsPrey?: boolean
  explicitHostileIntent?: boolean
}

export const REQUIRED_CONSCIOUS_CAPABILITIES: readonly (keyof ConsciousLifeCapabilities)[] = [
  'perception',
  'memory',
  'emotion',
  'learning',
  'decision',
  'agency',
  'relationships',
  'skills',
] as const

const clamp01 = (value: number) =>
  Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0))

export function assertConsciousLifeRegistration(
  input: ConsciousLifeRegistration,
): ConsciousLifeRegistration {
  if (!input?.id?.trim()) throw new Error('Conscious life requires a stable id')
  if (!input?.speciesId?.trim()) throw new Error(`Conscious life ${input.id} requires a speciesId`)
  if (input.conscious !== true) throw new Error(`Conscious life ${input.id} must be conscious`)
  if (clamp01(input.agency01) <= 0) throw new Error(`Conscious life ${input.id} must retain agency`)
  if (clamp01(input.cognition01) <= 0) throw new Error(`Conscious life ${input.id} must retain cognition`)
  for (const capability of REQUIRED_CONSCIOUS_CAPABILITIES) {
    if (input.capabilities?.[capability] !== true) {
      throw new Error(`Conscious life ${input.id} missing required capability: ${capability}`)
    }
  }
  return {
    ...input,
    agency01: clamp01(input.agency01),
    cognition01: clamp01(input.cognition01),
    capabilities: { ...input.capabilities },
    skillIds: [...(input.skillIds ?? [])],
  }
}

export function canConsciousLifeInitiateAggression(context: AggressionContext): boolean {
  if (context.explicitHostileIntent) return context.doctrine === 'hostile'
  if (context.provoked || context.protectingSelf) return context.doctrine !== 'pacifist'
  if (context.protectingOther) return context.doctrine === 'protective' || context.doctrine === 'territorial' || context.doctrine === 'hostile'
  if (context.territoryBreached) return context.doctrine === 'territorial' || context.doctrine === 'hostile'
  if (context.targetIsPrey) return context.doctrine === 'predatory' || context.doctrine === 'hostile'
  return context.doctrine === 'hostile'
}

export function defaultConsciousCapabilities(): ConsciousLifeCapabilities {
  return {
    perception: true,
    memory: true,
    emotion: true,
    learning: true,
    decision: true,
    agency: true,
    relationships: true,
    skills: true,
  }
}

export const INYRON_CONSCIOUS_LIFE_LAW = Object.freeze({
  protocol: INYRON_CONSCIOUS_LIFE_PROTOCOL,
  absolute: true,
  statement: 'No living being in INYRON exists without consciousness.',
  requiredCapabilities: REQUIRED_CONSCIOUS_CAPABILITIES,
})
